import { Metadata } from 'next';

const SITE_NAME = 'GoGedit';
const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';
const DEFAULT_IMAGE = `${SITE_URL}/assets/images/og-image.jpg`;

interface MetadataParams {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  keywords?: string[];
}

export function generateMetadata(params: MetadataParams): Metadata {
  const {
    title,
    description,
    path = '',
    image = DEFAULT_IMAGE,
    type = 'website',
    publishedTime,
    modifiedTime,
    author,
    keywords = [],
  } = params;

  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const url = `${SITE_URL}${path}`;

  return {
    title: fullTitle,
    description,
    keywords: keywords.length > 0 ? keywords.join(', ') : undefined,
    authors: author ? [{ name: author }] : undefined,
    openGraph: {
      title: fullTitle,
      description,
      url,
      siteName: SITE_NAME,
      images: [
        {
          url: image,
          width: 1200,
          height: 630,
          alt: title,
        },
      ],
      locale: 'en_CA',
      type,
      ...(publishedTime && { publishedTime }),
      ...(modifiedTime && { modifiedTime }),
    },
    twitter: {
      card: 'summary_large_image',
      title: fullTitle,
      description,
      images: [image],
    },
    alternates: {
      canonical: url,
    },
  };
}

export function generateLocalBusinessSchema(params: {
  name: string;
  description: string;
  city: string;
  province: string;
  url: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `${SITE_NAME} - ${params.city}`,
    description: params.description,
    url: params.url,
    areaServed: {
      '@type': 'City',
      name: params.city,
      containedInPlace: {
        '@type': 'State',
        name: params.province,
      },
    },
    serviceType: 'On-Demand Moving and Delivery Services',
    priceRange: '$$',
  };
}

export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/assets/images/logo.png`,
    description: 'On-demand moving and delivery services for urban logistics',
    sameAs: [
      'https://www.facebook.com/gogedit',
      'https://twitter.com/gogedit',
      'https://www.linkedin.com/company/gogedit',
      'https://www.instagram.com/gogedit',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      availableLanguage: ['English', 'French'],
    },
  };
}

export function generateServiceSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'GoGedit On-Demand Moving Service',
    description: 'On-demand moving and delivery services for furniture, appliances, and marketplace purchases',
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    areaServed: {
      '@type': 'Country',
      name: 'Canada',
    },
    serviceType: 'Moving and Delivery',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      priceSpecification: {
        '@type': 'PriceSpecification',
        priceCurrency: 'CAD',
      },
    },
  };
}

export function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function generateArticleSchema(params: {
  title: string;
  description: string;
  author: string;
  publishedTime: string;
  modifiedTime?: string;
  url: string;
  image?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: params.title,
    description: params.description,
    author: {
      '@type': 'Person',
      name: params.author,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/assets/images/logo.png`,
      },
    },
    datePublished: params.publishedTime,
    dateModified: params.modifiedTime || params.publishedTime,
    url: params.url,
    image: params.image || DEFAULT_IMAGE,
  };
}

export function generateFAQSchema(faqs: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}
